import React, { useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft, Camera, CheckCircle2, AlertTriangle, Send, Eye, ChevronDown,
  ChevronRight, Circle, CircleCheck, CircleMinus, Flag, RotateCcw,
  Download, Trash2, X, Loader2, FileCheck2,
} from "lucide-react";
import { supabase, apiFetch } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import { useGps } from "@/features/gps/useGps";
import { GpsIndicator } from "@/features/gps/GpsIndicator";
import { Card, Pill, Button, Field, Input, Textarea, Select, Spinner, EmptyState } from "@/components/ui";
import { StickyBottomBar } from "@/components/layout/StickyBottomBar";
import { PhotoCaptureButton } from "@/features/photos/PhotoCaptureButton";
import { fmtDateTime } from "@/lib/dates";
import type { WatermarkContext } from "@/features/photos/capture";

// =============================================================================
// Acceptance Pack Detail
// =============================================================================
// Two modes driven by pack status + user role:
//   FIELD     — pack.status in ('draft','in_progress') and user has access.
//               Shows the checklist; tap items to capture photos inline,
//               mark items complete / na. When 100% done, submit for QA.
//   QA        — pack.status = 'qa_review' and user is manager+.
//               Shows each item's photos at full size, with flag / approve
//               buttons. When all items approved, publish.
//   READ-ONLY — anything else (published, accepted, rejected_by_client).
// =============================================================================

interface PackItem {
  pack_item_id: string;
  pack_id: string;
  status: "pending" | "in_progress" | "complete" | "na" | "flagged" | "rejected";
  notes: string | null;
  na_reason: string | null;
  qa_status: "pending" | "approved" | "flagged_for_redo" | "rejected" | null;
  qa_notes: string | null;
  redo_count: number;
  sort_order: number;
  template_item: {
    item_number: string;
    category: string | null;
    item_label: string;
    item_description: string | null;
    requires_photo: boolean;
    min_photos: number;
    max_photos: number;
    is_mandatory: boolean;
  };
  photos: PackPhoto[];
}

interface PackPhoto {
  photo_id: string;
  watermarked_file_id: string | null;
  captured_at: string;
  gps_lat: number | null;
  gps_lng: number | null;
  watermarked_url?: string;       // populated on demand via signed URL
  is_rejected: boolean;
}

export default function PackDetailScreen() {
  const { packId } = useParams<{ packId: string }>();
  const user = useRequireAuth();
  const navigate = useNavigate();
  const gps = useGps(true);
  const qc = useQueryClient();
  const [openItem, setOpenItem] = useState<string | null>(null);
  const [qaReviewItem, setQaReviewItem] = useState<string | null>(null);

  // -- Pack header -----------------------------------------------------------
  const { data: pack, isLoading } = useQuery({
    queryKey: ["pack", packId],
    enabled: !!packId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("acceptance_packs")
        .select(`
          pack_id, pack_number, pack_title, status, items_total, items_complete,
          started_at, submitted_for_qa_at, qa_approved_at, published_at,
          sharepoint_url, r2_folder_path, client_rejected_at, client_rejection_notes,
          project_id,
          projects(project_code, project_name, site_id, sites(site_code, site_name)),
          template:checklist_templates(template_code, display_name, version)
        `)
        .eq("pack_id", packId!)
        .maybeSingle();
      if (error) throw error;
      return data as any;
    },
  });

  // -- Items with their photos ----------------------------------------------
  const { data: items = [], isLoading: itemsLoading } = useQuery({
    queryKey: ["pack_items", packId],
    enabled: !!packId,
    queryFn: async () => {
      // Items + template metadata
      const { data: itemRows, error } = await supabase
        .from("acceptance_checklist_items")
        .select(`
          pack_item_id, pack_id, status, notes, na_reason, qa_status, qa_notes,
          redo_count, sort_order,
          template_item:checklist_template_items(
            item_number, category, item_label, item_description,
            requires_photo, min_photos, max_photos, is_mandatory
          )
        `)
        .eq("pack_id", packId!)
        .order("sort_order");
      if (error) throw error;

      // Photos
      const packItemIds = (itemRows ?? []).map((r: any) => r.pack_item_id);
      let photos: any[] = [];
      if (packItemIds.length > 0) {
        const { data: photoRows } = await supabase
          .from("acceptance_photos")
          .select("photo_id, pack_item_id, watermarked_file_id, captured_at, gps_lat, gps_lng, is_rejected")
          .in("pack_item_id", packItemIds)
          .eq("is_rejected", false)
          .order("captured_at");
        photos = photoRows ?? [];
      }

      // Join photos onto items client-side
      return (itemRows ?? []).map((r: any): PackItem => ({
        ...r,
        photos: photos.filter(p => p.pack_item_id === r.pack_item_id),
      }));
    },
    refetchInterval: 60_000,
  });

  // -- Group by category ----------------------------------------------------
  const grouped = useMemo(() => {
    const g: Record<string, PackItem[]> = {};
    for (const it of items) {
      const cat = it.template_item.category ?? "Other";
      (g[cat] ||= []).push(it);
    }
    return g;
  }, [items]);

  // -- Derived stats --------------------------------------------------------
  const complete = items.filter(i => i.status === "complete" || i.status === "na").length;
  const pct = items.length > 0 ? Math.round((complete / items.length) * 100) : 0;
  const flagged = items.filter(i => i.status === "flagged" || i.qa_status === "flagged_for_redo").length;
  const pendingQa = items.filter(i => i.qa_status !== "approved" && (i.status === "complete" || i.status === "na")).length;
  const allQaApproved = items.length > 0 && items.every(i => i.qa_status === "approved" || i.status === "na");

  // -- Mutations: item state changes ---------------------------------------
  const itemMut = useMutation({
    mutationFn: async ({ pack_item_id, patch }: { pack_item_id: string; patch: Partial<PackItem> }) => {
      const { error } = await supabase.from("acceptance_checklist_items").update(patch).eq("pack_item_id", pack_item_id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["pack_items", packId] }),
  });

  // -- Mutations: pack lifecycle -------------------------------------------
  const packMut = useMutation({
    mutationFn: async (patch: Record<string, any>) => {
      const { error } = await supabase.from("acceptance_packs").update(patch).eq("pack_id", packId!);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["pack", packId] });
      qc.invalidateQueries({ queryKey: ["pack_items", packId] });
    },
  });

  function submitForQA() {
    packMut.mutate({
      status: "qa_review",
      submitted_for_qa_at: new Date().toISOString(),
    });
  }

  function publish() {
    packMut.mutate({
      status: "ready_to_publish",
      qa_approved_at: new Date().toISOString(),
      qa_reviewer_employee_id: user?.employee_id,
    });
    // Queue the SharePoint publish event via the notifications outbox.
    // The queue-bridge Worker fans this out onto the sharepoint-publish queue.
    supabase.from("notifications").insert({
      channel: "in_app",
      event_type: "acceptance_pack.qa_approved",
      recipient_hint: "worker:sharepoint",
      payload: { pack_id: packId, project_code: pack?.projects?.project_code },
      related_table: "acceptance_packs",
      related_id: packId,
    });
  }

  // -- UI state -------------------------------------------------------------
  if (isLoading || !pack) {
    return <div className="py-20 flex justify-center"><Spinner /></div>;
  }

  const mode: "field" | "qa" | "readonly" = (() => {
    if (pack.status === "qa_review" && hasRole(user, "manager")) return "qa";
    if (pack.status === "draft" || pack.status === "in_progress") return "field";
    return "readonly";
  })();

  const wmCtx: WatermarkContext = {
    project_code: pack.projects?.project_code,
    site_code: pack.projects?.sites?.site_code,
    pack_id: pack.pack_id,
    employee_name: user ? `${user.first_name ?? ""} ${user.last_name ?? ""}`.trim() : undefined,
  };

  // -- Render ---------------------------------------------------------------
  return (
    <div className="max-w-3xl mx-auto p-4 md:p-6 pb-32">
      <button onClick={() => navigate("/packs")} className="text-text-muted text-[12px] inline-flex items-center gap-1.5 mb-3">
        <ArrowLeft size={14}/> Packs
      </button>

      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3 mb-1">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-[13px] text-accent">{pack.pack_number}</span>
            <Pill
              label={pack.status.replace(/_/g, " ")}
              tone={
                pack.status === "published" || pack.status === "accepted" ? "approved" :
                pack.status === "qa_review" ? "warning" :
                pack.status === "rejected_by_client" ? "critical" :
                pack.status === "ready_to_publish" ? "approved" :
                "mobilising"
              }
            />
          </div>
          <h1 className="text-[22px] font-semibold tracking-tight mt-1">{pack.pack_title}</h1>
          <div className="text-text-muted text-[13px] mt-1">
            {pack.projects?.project_code ?? "—"} · {pack.projects?.sites?.site_code ?? "—"} · {pack.template?.display_name}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {mode === "field" && <GpsIndicator gps={gps} />}
          {pack.sharepoint_url && (
            <a href={pack.sharepoint_url} target="_blank" rel="noreferrer">
              <Button variant="secondary" icon={Eye} size="sm">Open in SharePoint</Button>
            </a>
          )}
          {(pack.status === "published" || pack.status === "accepted" || pack.status === "rejected_by_client") && hasRole(user, "manager") && (
            <Button variant="secondary" icon={FileCheck2} size="sm" onClick={() => navigate(`/packs/${pack.pack_id}/sign-off`)}>
              {pack.status === "accepted" ? "View sign-off" : pack.status === "rejected_by_client" ? "View rejection" : "Record sign-off"}
            </Button>
          )}
        </div>
      </div>

      {/* Rejection banner */}
      {pack.status === "rejected_by_client" && (
        <div className="mt-4 bg-danger/10 border border-danger/30 rounded-md p-3">
          <div className="flex items-start gap-2">
            <AlertTriangle size={14} className="text-danger mt-0.5 shrink-0" />
            <div className="text-[13px] text-danger">
              <div className="font-medium">Client rejected this pack</div>
              <div className="text-[12px] mt-1 opacity-80">{pack.client_rejection_notes ?? "No rejection notes supplied."}</div>
            </div>
          </div>
        </div>
      )}

      {/* Progress */}
      <Card className="mt-4 mb-4">
        <div className="p-4 space-y-2">
          <div className="flex items-center justify-between text-[12px] font-mono">
            <span className="text-text-muted uppercase tracking-wider2">Progress</span>
            <span className="text-text">{complete} / {items.length} {mode === "qa" && `· ${pendingQa} awaiting QA`}</span>
          </div>
          <div className="h-2 bg-ink-raised rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-[width] duration-500 ${
                mode === "qa" && pendingQa > 0 ? "bg-warn" :
                pack.status === "rejected_by_client" ? "bg-danger" :
                "bg-accent"
              }`}
              style={{ width: `${pct}%` }}
            />
          </div>
          {flagged > 0 && (
            <div className="text-[11px] text-danger font-mono flex items-center gap-1.5">
              <Flag size={10} /> {flagged} item(s) flagged for re-shoot
            </div>
          )}
        </div>
      </Card>

      {/* Items list */}
      {itemsLoading ? (
        <div className="py-10 flex justify-center"><Spinner /></div>
      ) : items.length === 0 ? (
        <Card><EmptyState title="No checklist items" description="This pack has no items — check the template." /></Card>
      ) : (
        Object.entries(grouped).map(([cat, list]) => (
          <section key={cat} className="mb-4">
            <h2 className="text-[11px] font-semibold uppercase tracking-wider2 text-text-muted mb-2 px-1">{cat}</h2>
            <Card>
              {list.map((item, i) => (
                <ChecklistItemRow
                  key={item.pack_item_id}
                  item={item}
                  last={i === list.length - 1}
                  mode={mode}
                  open={openItem === item.pack_item_id}
                  onToggle={() => setOpenItem(openItem === item.pack_item_id ? null : item.pack_item_id)}
                  onStatusChange={(status) => itemMut.mutate({ pack_item_id: item.pack_item_id, patch: { status } })}
                  onNotesChange={(notes) => itemMut.mutate({ pack_item_id: item.pack_item_id, patch: { notes } })}
                  onNaReasonChange={(na_reason) => itemMut.mutate({ pack_item_id: item.pack_item_id, patch: { na_reason } })}
                  onPhotoCaptured={() => qc.invalidateQueries({ queryKey: ["pack_items", packId] })}
                  onQaReview={() => setQaReviewItem(item.pack_item_id)}
                  wmCtx={wmCtx}
                  gps={gps}
                />
              ))}
            </Card>
          </section>
        ))
      )}

      {/* QA review drawer */}
      {qaReviewItem && (
        <QaReviewDrawer
          packItemId={qaReviewItem}
          packId={packId!}
          userId={user?.user_id}
          employeeId={user?.employee_id}
          onClose={() => setQaReviewItem(null)}
          onChanged={() => {
            qc.invalidateQueries({ queryKey: ["pack_items", packId] });
            qc.invalidateQueries({ queryKey: ["pack", packId] });
          }}
        />
      )}

      {/* Footer actions */}
      <StickyBottomBar className="p-3 flex justify-center">
        <div className="flex gap-2 max-w-3xl w-full items-center">
          <div className="flex-1 text-[11px] font-mono text-text-muted hidden sm:block">
            {mode === "field" && pct < 100 && `${items.length - complete} item(s) remaining`}
            {mode === "field" && pct === 100 && "All items complete — submit for QA"}
            {mode === "qa" && pendingQa > 0 && `${pendingQa} item(s) awaiting QA review`}
            {mode === "qa" && pendingQa === 0 && "All items reviewed — ready to publish"}
          </div>

          {mode === "field" && (
            <Button
              size="lg"
              icon={Send}
              disabled={pct < 100 || packMut.isPending}
              onClick={submitForQA}
              className="flex-1 sm:flex-none justify-center"
            >
              {packMut.isPending ? "Submitting…" : "Submit for QA"}
            </Button>
          )}
          {mode === "qa" && (
            <Button
              size="lg"
              icon={FileCheck2}
              disabled={!allQaApproved || packMut.isPending}
              onClick={publish}
              className="flex-1 sm:flex-none justify-center"
            >
              {packMut.isPending ? "Publishing…" : "Approve & publish"}
            </Button>
          )}
          {mode === "readonly" && (
            <Button size="lg" variant="secondary" onClick={() => navigate("/packs")} className="flex-1 sm:flex-none justify-center">
              Back to packs
            </Button>
          )}
        </div>
      </StickyBottomBar>
    </div>
  );
}

// =============================================================================
// Single checklist item — the bread & butter row
// =============================================================================

interface RowProps {
  item: PackItem;
  last: boolean;
  mode: "field" | "qa" | "readonly";
  open: boolean;
  onToggle: () => void;
  onStatusChange: (s: PackItem["status"]) => void;
  onNotesChange: (n: string) => void;
  onNaReasonChange: (r: string) => void;
  onPhotoCaptured: () => void;
  onQaReview: () => void;
  wmCtx: WatermarkContext;
  gps: ReturnType<typeof useGps>;
}

function ChecklistItemRow({
  item, last, mode, open, onToggle,
  onStatusChange, onNotesChange, onNaReasonChange,
  onPhotoCaptured, onQaReview, wmCtx, gps,
}: RowProps) {
  const ti = item.template_item;
  const photoCount = item.photos.length;
  const enoughPhotos = !ti.requires_photo || photoCount >= ti.min_photos;
  const flaggedByQa = item.qa_status === "flagged_for_redo";

  function statusIcon() {
    if (item.status === "complete" && item.qa_status === "approved")  return <CircleCheck size={18} className="text-ok" />;
    if (item.status === "complete") return <CircleCheck size={18} className="text-accent" />;
    if (item.status === "na")       return <CircleMinus size={18} className="text-text-muted" />;
    if (item.status === "flagged" || flaggedByQa) return <Flag size={16} className="text-danger" />;
    return <Circle size={18} className="text-text-dim" />;
  }

  return (
    <div className={last ? "" : "border-b border-line"}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-start gap-3 p-4 text-left hover:bg-ink-hover transition-colors"
      >
        <div className="mt-0.5 shrink-0">{statusIcon()}</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[11px] font-mono text-text-muted">{ti.item_number}</span>
            {ti.is_mandatory && <Pill label="req" tone="default" small />}
            {item.qa_status === "approved" && <Pill label="QA ✓" tone="approved" small />}
            {flaggedByQa && <Pill label="re-shoot" tone="critical" small />}
          </div>
          <div className="text-[13px] text-text mt-1">{ti.item_label}</div>
          {ti.requires_photo && (
            <div className="text-[11px] font-mono text-text-muted mt-1">
              <Camera size={10} className="inline mr-1 -mt-0.5" />
              {photoCount}{ti.min_photos > 1 ? ` / ${ti.min_photos} min` : ""} photo{photoCount !== 1 ? "s" : ""}
              {ti.requires_photo && photoCount < ti.min_photos && <span className="text-warn ml-2">· need more</span>}
            </div>
          )}
          {flaggedByQa && item.qa_notes && (
            <div className="text-[12px] text-danger bg-danger/10 border border-danger/20 rounded px-2 py-1.5 mt-2">
              QA note: {item.qa_notes}
            </div>
          )}
        </div>
        <ChevronDown size={14} className={`text-text-dim mt-1 transition-transform shrink-0 ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="px-4 pb-4 pt-0 space-y-3 border-t border-line bg-ink/30">
          {ti.item_description && (
            <div className="text-[12px] text-text-muted pt-3">{ti.item_description}</div>
          )}

          {/* Photo strip */}
          {item.photos.length > 0 && (
            <PhotoStrip photos={item.photos} />
          )}

          {/* Field mode actions */}
          {mode === "field" && (
            <div className="space-y-3 pt-1">
              {ti.requires_photo && (
                <PhotoCaptureButton
                  ctx={{ ...wmCtx, pack_item_id: item.pack_item_id }}
                  gps={gps.fix}
                  onCaptured={() => onPhotoCaptured()}
                  multiple={ti.max_photos > 1}
                  disabled={ti.max_photos > 0 && photoCount >= ti.max_photos}
                  label={photoCount === 0 ? "Capture photo" : "Add another"}
                />
              )}

              <div>
                <label className="text-[11px] uppercase tracking-wider2 text-text-muted font-medium">Notes (optional)</label>
                <textarea
                  value={item.notes ?? ""}
                  onChange={e => onNotesChange(e.target.value)}
                  placeholder="Any specifics worth recording"
                  rows={2}
                  className="w-full mt-1.5 px-3 py-2 bg-ink-raised border border-line rounded-md text-text text-[13px] outline-none focus:border-accent resize-y"
                />
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                <Button
                  icon={CheckCircle2}
                  onClick={() => onStatusChange("complete")}
                  disabled={!enoughPhotos}
                  size="sm"
                >
                  Mark complete
                </Button>
                {!ti.is_mandatory && (
                  <Button
                    variant="secondary"
                    icon={CircleMinus}
                    onClick={() => {
                      const reason = prompt("N/A reason?");
                      if (reason) {
                        onNaReasonChange(reason);
                        onStatusChange("na");
                      }
                    }}
                    size="sm"
                  >
                    Not applicable
                  </Button>
                )}
                {item.status === "complete" && (
                  <Button
                    variant="ghost"
                    icon={RotateCcw}
                    onClick={() => onStatusChange("in_progress")}
                    size="sm"
                  >
                    Reopen
                  </Button>
                )}
              </div>

              {!enoughPhotos && ti.requires_photo && (
                <div className="text-[11px] text-warn font-mono">
                  Capture {ti.min_photos - photoCount} more photo(s) before marking complete.
                </div>
              )}
            </div>
          )}

          {/* QA mode — review button */}
          {mode === "qa" && (item.status === "complete" || item.status === "na") && (
            <div className="flex items-center gap-2">
              <Button
                icon={Eye}
                size="sm"
                onClick={onQaReview}
              >
                {item.qa_status === "approved" ? "Review again" : "Review"}
              </Button>
              {item.qa_status === "approved" && <Pill label="approved" tone="approved" small />}
              {item.qa_status === "flagged_for_redo" && <Pill label="awaiting re-shoot" tone="warning" small />}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Photo strip — tappable thumbnails, lazy-loads signed URLs
// =============================================================================

function PhotoStrip({ photos }: { photos: PackPhoto[] }) {
  return (
    <div className="flex gap-2 overflow-x-auto py-2 -mx-1 px-1">
      {photos.map(p => <PhotoThumb key={p.photo_id} photo={p} />)}
    </div>
  );
}

function PhotoThumb({ photo }: { photo: PackPhoto }) {
  const { data: signed } = useQuery({
    queryKey: ["signed_url", photo.watermarked_file_id],
    enabled: !!photo.watermarked_file_id,
    staleTime: 10 * 60_000,
    queryFn: async () => {
      // Look up the R2 object_key
      const { data: f } = await supabase
        .from("files")
        .select("r2_bucket, r2_object_key")
        .eq("file_id", photo.watermarked_file_id!)
        .maybeSingle();
      if (!f) return null;
      const res = await apiFetch("/sign-download", {
        method: "POST",
        body: JSON.stringify({
          bucket: f.r2_bucket,
          object_key: f.r2_object_key,
          expires_in_seconds: 600,
        }),
      });
      if (!res.ok) return null;
      const { download_url } = await res.json();
      return download_url as string;
    },
  });

  return (
    <div className="w-24 h-24 shrink-0 rounded bg-ink-raised border border-line overflow-hidden relative">
      {signed ? (
        <img src={signed} alt="" className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <Loader2 size={14} className="animate-spin text-text-muted" />
        </div>
      )}
      <div className="absolute bottom-0 inset-x-0 bg-black/60 text-[9px] font-mono text-white text-center py-0.5">
        {fmtDateTime(photo.captured_at).split("·")[1]?.trim() ?? ""}
      </div>
    </div>
  );
}

// =============================================================================
// QA review drawer — full-size photo viewer + approve/flag actions
// =============================================================================

function QaReviewDrawer({
  packItemId, packId, userId, employeeId, onClose, onChanged,
}: {
  packItemId: string;
  packId: string;
  userId?: string;
  employeeId?: string | null;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [flagReason, setFlagReason] = useState("");
  const [mode, setMode] = useState<"review" | "flag">("review");

  const { data: item } = useQuery({
    queryKey: ["qa_item", packItemId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("acceptance_checklist_items")
        .select(`
          pack_item_id, status, notes, qa_status, qa_notes,
          template_item:checklist_template_items(item_number, category, item_label, item_description)
        `)
        .eq("pack_item_id", packItemId)
        .maybeSingle();
      if (error) throw error;
      return data as any;
    },
  });

  const { data: photos = [] } = useQuery({
    queryKey: ["qa_photos", packItemId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("acceptance_photos")
        .select("photo_id, watermarked_file_id, captured_at, gps_lat, gps_lng")
        .eq("pack_item_id", packItemId)
        .eq("is_rejected", false)
        .order("captured_at");
      if (error) throw error;
      return data as any[];
    },
  });

  const approveMut = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.from("acceptance_checklist_items").update({
        qa_status: "approved",
        qa_notes: null,
        qa_reviewer_employee_id: employeeId,
        qa_reviewed_at: new Date().toISOString(),
      }).eq("pack_item_id", packItemId);
      if (error) throw error;
    },
    onSuccess: () => { onChanged(); onClose(); },
  });

  const flagMut = useMutation({
    mutationFn: async () => {
      if (!flagReason.trim()) throw new Error("reason_required");
      const { error } = await supabase.from("acceptance_checklist_items").update({
        qa_status: "flagged_for_redo",
        qa_notes: flagReason.trim(),
        qa_reviewer_employee_id: employeeId,
        qa_reviewed_at: new Date().toISOString(),
        // Bump status back so the field crew sees this in their 'active' queue
        status: "flagged",
        redo_count: (item?.redo_count ?? 0) + 1,
      }).eq("pack_item_id", packItemId);
      if (error) throw error;
    },
    onSuccess: () => { onChanged(); onClose(); },
  });

  if (!item) {
    return (
      <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex flex-col" onClick={onClose}>
      <div
        className="bg-ink-card border-t md:border border-line w-full max-w-4xl mx-auto flex-1 flex flex-col overflow-hidden md:my-8 md:rounded-lg"
        onClick={e => e.stopPropagation()}
      >
        <div className="p-4 border-b border-line flex items-center justify-between shrink-0">
          <div className="min-w-0">
            <div className="font-mono text-[11px] text-text-muted">{item.template_item.item_number} · {item.template_item.category}</div>
            <h3 className="text-[16px] font-semibold mt-0.5 truncate">{item.template_item.item_label}</h3>
            {item.notes && <div className="text-[12px] text-text-muted mt-1 line-clamp-1">Field note: {item.notes}</div>}
          </div>
          <button onClick={onClose} className="text-text-muted hover:text-text p-1 ml-3"><X size={18}/></button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-ink">
          {photos.length === 0 ? (
            <div className="py-20 text-center text-text-muted">No photos captured for this item.</div>
          ) : (
            photos.map(p => <QaPhotoView key={p.photo_id} photo={p} />)
          )}
        </div>

        <div className="p-4 border-t border-line bg-ink-raised shrink-0">
          {mode === "review" ? (
            <div className="flex items-center justify-end gap-2">
              <Button variant="danger" icon={Flag} onClick={() => setMode("flag")}>Flag for re-shoot</Button>
              <Button icon={CheckCircle2} onClick={() => approveMut.mutate()} disabled={approveMut.isPending}>
                Approve
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              <label className="text-[11px] uppercase tracking-wider2 text-text-muted font-medium">Re-shoot reason</label>
              <Textarea
                value={flagReason}
                onChange={e => setFlagReason(e.target.value)}
                placeholder="What needs to change — visible to field crew"
                rows={2}
                autoFocus
              />
              <div className="flex justify-end gap-2 pt-1">
                <Button variant="secondary" onClick={() => setMode("review")}>Back</Button>
                <Button variant="danger" icon={Flag} onClick={() => flagMut.mutate()} disabled={!flagReason.trim() || flagMut.isPending}>
                  Send back for re-shoot
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function QaPhotoView({ photo }: { photo: { photo_id: string; watermarked_file_id: string | null; captured_at: string; gps_lat: number | null; gps_lng: number | null } }) {
  const { data: signed } = useQuery({
    queryKey: ["signed_url_full", photo.watermarked_file_id],
    enabled: !!photo.watermarked_file_id,
    staleTime: 10 * 60_000,
    queryFn: async () => {
      const { data: f } = await supabase
        .from("files")
        .select("r2_bucket, r2_object_key")
        .eq("file_id", photo.watermarked_file_id!)
        .maybeSingle();
      if (!f) return null;
      const res = await apiFetch("/sign-download", {
        method: "POST",
        body: JSON.stringify({ bucket: f.r2_bucket, object_key: f.r2_object_key, expires_in_seconds: 900 }),
      });
      if (!res.ok) return null;
      const { download_url } = await res.json();
      return download_url as string;
    },
  });

  return (
    <Card>
      <div className="aspect-video bg-black flex items-center justify-center">
        {signed ? (
          <img src={signed} alt="" className="max-w-full max-h-full object-contain" />
        ) : (
          <Loader2 size={18} className="animate-spin text-text-muted" />
        )}
      </div>
      <div className="p-3 flex items-center justify-between gap-3 text-[11px] font-mono text-text-muted">
        <span>Captured {fmtDateTime(photo.captured_at)}</span>
        {photo.gps_lat != null && (
          <span>
            {photo.gps_lat.toFixed(4)}, {photo.gps_lng?.toFixed(4)}
          </span>
        )}
      </div>
    </Card>
  );
}
